import React, { useState, useEffect } from 'react';
import './CryptoHoldings.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'x-auth-token': token } : {};
};

function CryptoHoldings() {
  const [cryptoData, setCryptoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCrypto = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crypto`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch crypto data');
      const data = await response.json();
      setCryptoData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCrypto();
    const interval = setInterval(fetchCrypto, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatNumber = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPrice = (price) => {
    if (price >= 1000) {
      return formatNumber(price);
    }
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: price < 1 ? 4 : 2
    }).format(price);
  };

  const formatAmount = (amount) => {
    if (amount >= 1) {
      return amount.toLocaleString('en-US', { maximumFractionDigits: 4 });
    }
    return amount.toLocaleString('en-US', { maximumFractionDigits: 8 });
  };

  if (loading) {
    return (
      <div className="crypto-holdings loading">
        <div className="crypto-header">
          <h2>Crypto</h2>
        </div>
        <div className="crypto-loading">fetching prices...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="crypto-holdings error">
        <div className="crypto-header">
          <h2>Crypto</h2>
        </div>
        <div className="crypto-error">
          <p>error: connection failed</p>
          <button onClick={fetchCrypto}>retry</button>
        </div>
      </div>
    );
  }

  if (!cryptoData || cryptoData.holdings.length === 0) {
    return (
      <div className="crypto-holdings empty">
        <div className="crypto-header">
          <h2>Crypto</h2>
        </div>
        <div className="crypto-empty">
          <p>no holdings configured</p>
          <p className="crypto-hint">edit CRYPTO_HOLDINGS in server</p>
        </div>
      </div>
    );
  }

  return (
    <div className="crypto-holdings">
      <div className="crypto-header">
        <h2>Crypto</h2>
        <div className="crypto-total">
          <span className="total-value">${formatNumber(cryptoData.totalValue)}</span>
          <span className={`total-change ${cryptoData.change24h >= 0 ? 'positive' : 'negative'}`}>
            {cryptoData.change24h >= 0 ? '+' : ''}{cryptoData.change24h.toFixed(2)}%
          </span>
        </div>
      </div>

      <div className="crypto-list">
        {cryptoData.holdings.map(holding => (
          <div key={holding.id} className="crypto-item">
            <div className="crypto-coin">
              <span className="coin-symbol">{holding.symbol}</span>
              <span className="coin-amount">{formatAmount(holding.amount)}</span>
            </div>
            <div className="crypto-price">
              <span className="price-current">${formatPrice(holding.price)}</span>
            </div>
            <div className="crypto-value">
              ${formatNumber(holding.value)}
            </div>
          </div>
        ))}
      </div>

      {cryptoData.lastUpdated && (
        <div className="crypto-footer">
          {new Date(cryptoData.lastUpdated).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}

export default CryptoHoldings;
